import { COUNTRIES } from "../data/countries";

/**
 * Checks for the Get Free Quote form (components/QuoteForm). The Geo pages
 * run the very same code: scripts/geo/sync-validation.mjs compiles this file
 * into their pages, so change the rules here only.
 *
 * Two layers: FILTERS run as the visitor types and simply drop what cannot
 * belong in the field (digits in a name, letters in an age or a phone
 * number) and cap its length; validateQuote runs on Submit and names every
 * field that is still wrong.
 *
 * Every field is required (client, 2026-10-28): an empty one gets its own message.
 *
 * Beyond the format of each field, Submit also checks (client, 2026-10-28):
 *   - the phone number has the right number of digits for its country code,
 *     and that code is the selected country's;
 *   - no test or junk entries ("test", "asdf", "aaaa", keyboard rows…) in the
 *     name, centre, treatment or condition;
 *   - the email: a likely typo of a common provider is pointed out (and
 *     accepted if the visitor sends it again unchanged), throwaway inboxes are
 *     refused.
 */
export type QuoteFields = {
  fullName: string;
  email: string;
  country: string;
  age: string;
  phone: string;
  preferredCenter: string;
  treatment: string;
  travelDates: string;
  condition: string;
};
export type QuoteErrors = Partial<Record<keyof QuoteFields | "code", string>>;
export type QuoteOptions = {
  /** an email the visitor has sent again after being asked "did you mean…": accepted as typed */
  acceptEmail?: string;
};

const letters = (v: string) => (v.match(/\p{L}/gu) ?? []).length;
const digits = (v: string) => (v.match(/\d/g) ?? []).length;

export const FILTERS: Partial<Record<keyof QuoteFields | "code", (v: string) => string>> = {
  fullName: (v) => v.replace(/[^\p{L}\s.'-]/gu, "").replace(/\s{2,}/g, " ").slice(0, 60),
  email: (v) => v.replace(/\s/g, "").slice(0, 100),
  age: (v) => v.replace(/\D/g, "").slice(0, 3),
  code: (v) => {
    const d = v.replace(/\D/g, "").slice(0, 4);
    return d ? `+${d}` : v.trim().startsWith("+") ? "+" : "";
  },
  phone: (v) => v.replace(/[^\d\s-]/g, "").slice(0, 18),
  preferredCenter: (v) => v.replace(/[^\p{L}\s,.&/()-]/gu, "").slice(0, 80),
  treatment: (v) => v.replace(/[^\p{L}\d\s,.&/()'-]/gu, "").slice(0, 100),
  condition: (v) => v.slice(0, 1000),
};

/** today as YYYY-MM-DD in the visitor's own time zone */
export const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

/* ------------------------------------------------------------------ phone */

/**
 * How many digits a number has after the country code (its national
 * significant number, without a leading trunk 0), per code. Ranges cover
 * mobiles and landlines. A code not listed keeps the general 6–14.
 */
const PHONE_DIGITS: Record<string, [number, number]> = {
  "+1": [10, 10], "+7": [10, 10], "+20": [8, 10], "+27": [9, 9], "+30": [10, 10], "+31": [9, 9], "+32": [8, 9],
  "+33": [9, 9], "+34": [9, 9], "+36": [8, 9], "+39": [6, 11], "+40": [9, 9], "+41": [9, 9], "+43": [7, 13],
  "+44": [9, 10], "+45": [8, 8], "+46": [7, 10], "+47": [8, 8], "+48": [9, 9], "+49": [6, 13], "+51": [8, 9],
  "+52": [10, 10], "+54": [10, 11], "+55": [10, 11], "+56": [9, 9], "+57": [10, 10], "+58": [10, 10],
  "+60": [9, 10], "+61": [9, 9], "+62": [9, 12], "+63": [8, 10], "+64": [8, 10], "+65": [8, 8], "+66": [8, 9],
  "+81": [9, 10], "+82": [8, 10], "+84": [9, 10], "+86": [10, 11], "+90": [10, 10], "+91": [10, 10], "+92": [10, 10],
  "+94": [9, 9], "+98": [10, 10], "+212": [9, 9], "+230": [7, 8], "+233": [9, 9], "+234": [8, 10], "+251": [9, 9],
  "+254": [9, 9], "+255": [9, 9], "+256": [9, 9], "+351": [9, 9], "+352": [6, 11], "+353": [7, 9], "+354": [7, 7],
  "+356": [8, 8], "+357": [8, 8], "+358": [6, 11], "+359": [8, 9], "+370": [8, 8], "+371": [8, 8], "+372": [7, 8],
  "+380": [9, 9], "+381": [8, 9], "+385": [8, 9], "+386": [8, 8], "+420": [9, 9], "+421": [9, 9], "+880": [9, 10],
  "+960": [7, 7], "+961": [7, 8], "+962": [8, 9], "+964": [8, 10], "+965": [8, 8], "+966": [8, 9], "+968": [8, 8],
  "+971": [8, 9], "+972": [8, 9], "+973": [8, 8], "+974": [8, 8], "+977": [8, 10], "+998": [9, 9],
  "+93": [9, 9], "+95": [7, 10], "+213": [8, 9], "+216": [8, 8], "+221": [9, 9], "+225": [10, 10], "+237": [9, 9],
  "+250": [9, 9], "+260": [9, 9], "+263": [9, 9], "+375": [9, 9], "+376": [6, 9], "+993": [8, 8], "+994": [9, 9], "+995": [9, 9],
};
/** codes whose numbers keep their leading 0 after the country code */
const KEEPS_ZERO = new Set(["+39", "+225", "+378"]);

const codeOf = (country: string) => COUNTRIES.find(([n]) => n === country)?.[1];

function phoneError(code: string, phone: string): string | undefined {
  let d = phone.replace(/\D/g, "");
  if (!d) return "Please enter your phone or WhatsApp number.";
  if (d.startsWith("0") && !KEEPS_ZERO.has(code)) d = d.slice(1); // the trunk 0 people type before a local number
  const [min, max] = PHONE_DIGITS[code] ?? [6, 14];
  if (/^(\d)\1+$/.test(d) || "01234567890123456789".includes(d) || "98765432109876543210".includes(d)) return "Please enter a valid phone number.";
  if (d.length < min || d.length > max) {
    const want = min === max ? `${min} digits` : `${min}–${max} digits`;
    return `Please enter a valid phone number: numbers with ${code} have ${want} after the code.`;
  }
  if (code === "+1" && /^[01]/.test(d)) return "Please enter a valid phone number.";
  if (code === "+91" && !/^[1-9]/.test(d)) return "Please enter a valid phone number.";
  return undefined;
}

/* ------------------------------------------------------------------ junk */

/** words that are test or filler entries, never a name, centre or treatment */
const JUNK_WORDS = new Set([
  "test", "tests", "testing", "tester", "tst", "asdf", "asd", "asdasd", "sdf", "dsf", "qwe", "qwerty", "qwer", "zxc", "zxcv",
  // ("foo" and "bar" are left out: both are real surnames)
  "xyz", "xxx", "abc", "abcd", "abcde", "dummy", "sample", "fake", "demo", "lorem", "ipsum", "foobar",
  "null", "undefined", "blah", "random", "dfg", "jkl", "hjkl", "ghj", "fgh", "rty", "uio", "bnm",
]);
const ROWS = ["qwertyuiop", "asdfghjkl", "zxcvbnm", "qwertzuiop", "azertyuiop"];
const words = (v: string) => v.toLowerCase().split(/[^\p{L}]+/u).filter(Boolean);

/** a word that is plainly not one: junk, one letter repeated, a run of keyboard keys, or Latin letters with no vowel */
function junkWord(w: string): boolean {
  if (JUNK_WORDS.has(w)) return true;
  if (w.length >= 3 && /^(.)\1+$/u.test(w)) return true;
  if (w.length >= 5) for (const row of ROWS) for (let i = 0; i + 5 <= w.length; i++) {
    const part = w.slice(i, i + 5);
    if (row.includes(part) || row.split("").reverse().join("").includes(part)) return true;
  }
  if (w.length >= 5 && /^[a-z]+$/.test(w) && !/[aeiouy]/.test(w)) return true;
  return false;
}
/** a whole entry made of few different letters ("aaaa", "ababab") */
function lowVariety(v: string): boolean {
  const l = v.toLowerCase().replace(/[^\p{L}]/gu, "");
  return (l.length >= 4 && new Set(l).size <= 1) || (l.length >= 6 && new Set(l).size <= 2);
}
/** any junk word makes a short entry (name, centre, treatment) junk */
const junkShort = (v: string) => lowVariety(v) || words(v).some(junkWord);
/** a description is junk only if every word in it is (a real sentence may say "blood test") */
const junkLong = (v: string) => { const ws = words(v); return lowVariety(v) || (ws.length > 0 && ws.every(junkWord)); };

/* ------------------------------------------------------------------ email */

/** real providers: never "corrected" */
const KNOWN_DOMAINS = new Set([
  "gmail.com", "googlemail.com", "yahoo.com", "ymail.com", "rocketmail.com", "hotmail.com", "outlook.com", "live.com", "msn.com",
  "icloud.com", "me.com", "mac.com", "aol.com", "protonmail.com", "proton.me", "pm.me", "zoho.com", "mail.com", "email.com",
  "gmx.com", "gmx.de", "gmx.net", "gmx.at", "gmx.ch", "web.de", "t-online.de", "yandex.ru", "yandex.com", "mail.ru", "bk.ru",
  "inbox.ru", "list.ru", "rambler.ru", "ukr.net", "qq.com", "163.com", "126.com", "naver.com", "daum.net", "libero.it",
  "virgilio.it", "orange.fr", "wanadoo.fr", "free.fr", "sfr.fr", "laposte.net", "seznam.cz", "wp.pl", "o2.pl", "interia.pl",
  "rediffmail.com", "yahoo.co.in", "yahoo.co.uk", "yahoo.fr", "yahoo.de", "yahoo.es", "yahoo.it", "yahoo.ca", "yahoo.com.au",
  "hotmail.co.uk", "hotmail.fr", "hotmail.de", "hotmail.it", "hotmail.es", "outlook.in", "outlook.de", "live.co.uk",
  "btinternet.com", "sky.com", "bigpond.com", "comcast.net", "verizon.net", "att.net", "sbcglobal.net", "shaw.ca",
  "rogers.com", "telus.net", "xtra.co.nz", "tutanota.com", "fastmail.com",
]);
/** the providers whose misspellings are worth pointing out */
const POPULAR = ["gmail.com", "yahoo.com", "hotmail.com", "outlook.com", "icloud.com", "protonmail.com", "rediffmail.com", "yahoo.co.in", "hotmail.co.uk", "yahoo.co.uk"];
/** throwaway inboxes: a reply sent there is never read */
const DISPOSABLE = new Set([
  "mailinator.com", "guerrillamail.com", "guerrillamail.net", "sharklasers.com", "10minutemail.com", "10minutemail.net",
  "tempmail.com", "temp-mail.org", "tempmail.net", "tempmailo.com", "yopmail.com", "yopmail.net", "trashmail.com",
  "getnada.com", "nada.email", "dispostable.com", "maildrop.cc", "throwawaymail.com", "fakeinbox.com", "mailnesia.com",
  "mintemail.com", "emailondeck.com", "moakt.com", "burnermail.io", "spamgourmet.com", "mohmal.com", "mailcatch.com",
  "trashmail.de", "discard.email", "fakemail.net", "tempinbox.com", "mytemp.email", "inboxkitten.com", "1secmail.com",
]);
function distance(a: string, b: string): number {
  const d = Array.from({ length: a.length + 1 }, (_, i) => [i, ...Array(b.length).fill(0)]);
  for (let j = 1; j <= b.length; j++) d[0][j] = j;
  for (let i = 1; i <= a.length; i++) for (let j = 1; j <= b.length; j++) {
    d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
    if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1);
  }
  return d[a.length][b.length];
}
/** a likely meant address, when the domain looks like a misspelt common provider */
export function emailSuggestion(email: string): string | undefined {
  const at = email.lastIndexOf("@");
  if (at < 1) return undefined;
  const domain = email.slice(at + 1).toLowerCase();
  if (KNOWN_DOMAINS.has(domain)) return undefined;
  let best: string | undefined, bestD = 3;
  for (const p of POPULAR) { const dd = distance(domain, p); if (dd < bestD) { bestD = dd; best = p; } }
  return best && bestD <= 2 ? `${email.slice(0, at)}@${best}` : undefined;
}

/* ------------------------------------------------------------------ all */

export function validateQuote(f: QuoteFields, code: string, opts: QuoteOptions = {}): QuoteErrors {
  const e: QuoteErrors = {};
  const name = f.fullName.trim();
  if (!name) e.fullName = "Please enter your full name.";
  else if (letters(name) < 2) e.fullName = "Please enter your name in letters.";
  else if (junkShort(name)) e.fullName = "Please enter your real name.";

  const email = f.email.trim();
  if (!email) e.email = "Please enter your email address.";
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) e.email = "Please enter a valid email address.";
  else if (DISPOSABLE.has(email.slice(email.lastIndexOf("@") + 1).toLowerCase())) e.email = "Please use your regular email address: a temporary inbox cannot receive our reply.";
  else if (email !== opts.acceptEmail) {
    const s = emailSuggestion(email);
    if (s) e.email = `Did you mean ${s}? Correct it, or send again to keep it as typed.`;
  }

  if (!f.country) e.country = "Please select your country.";

  if (!f.age) e.age = "Please enter your age.";
  else {
    const n = Number(f.age);
    if (!Number.isInteger(n) || n < 1 || n > 120) e.age = "Please enter an age between 1 and 120.";
  }

  const c = code.trim();
  if (!c || c === "+") e.code = "Please enter your country code.";
  else if (!/^\+\d{1,4}$/.test(c)) e.code = "Please enter a valid country code.";
  else if (f.country && codeOf(f.country) && codeOf(f.country) !== c) e.code = `The code for ${f.country} is ${codeOf(f.country)}. Please check the code or the country.`;

  const pe = phoneError(c, f.phone.trim());
  if (pe) e.phone = pe;

  const centre = f.preferredCenter.trim();
  if (!centre) e.preferredCenter = "Please enter your preferred centre or city.";
  else if (letters(centre) < 2) e.preferredCenter = "Please enter a city or centre name.";
  else if (centre && junkShort(centre)) e.preferredCenter = "Please enter a real city or centre name.";

  const t = f.treatment.trim();
  if (!t) e.treatment = "Please enter the treatment you are interested in.";
  else if (letters(t) < 3 || digits(t) > letters(t)) e.treatment = "Please write the treatment in words.";
  else if (t && junkShort(t)) e.treatment = "Please write the treatment you are interested in.";

  const cond = f.condition.trim();
  if (!cond) e.condition = "Please describe your condition.";
  else if (letters(cond) < 3) e.condition = "Please describe your condition in words.";
  else if (cond && junkLong(cond)) e.condition = "Please describe your condition in a few words.";

  if (!f.travelDates) e.travelDates = "Please choose your preferred travel date.";
  else {
    const max = new Date();
    max.setFullYear(max.getFullYear() + 3);
    if (f.travelDates < today()) e.travelDates = "Please choose a date from today onwards.";
    else if (new Date(f.travelDates) > max) e.travelDates = "Please choose a date within the next three years.";
  }
  return e;
}
